import React, { useEffect } from "react";
import { useState } from "react";
import Cookies from "js-cookie";

import Form from "react-bootstrap/Form";
import { FaUser, FaIdCard, FaCalendarAlt, FaVenusMars, FaWeight, FaEnvelope, FaLock } from "react-icons/fa";
import { GiBodyHeight } from "react-icons/gi";
import { Col, Container, Row } from "react-bootstrap";

import { PALETTE } from "../../common/ThemeConstants";
import { useAuth } from "../../auth/hook";
import ContainerResponsive from "../../common/components/ContainerResponsive";
import SettingsHeader from "../components/SettingsHeader";
import AccountAvatar from "../../account/components/AccountAvatar";
import { getPercentage } from "../../account/utils/functions";
import { postRequest } from "../../api/axiosRequests";

export default function AccountSettings() {
  const auth = useAuth();
  const [user, setUser] = useState(auth.user);
  const [newPassword, setNewPassword] = useState("");
  const [newPasswordRepeated, setNewPasswordRepeated] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [isPasswordUpdating, setIsPasswordUpdating] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);
  const [cover, setCover] = useState(null);
  const [coverPreview, setCoverPreview] = useState(auth.user.cover || undefined);

  const warning = auth.user.cf === "";
  const percentage = getPercentage(user);

  useEffect(() => {
    window.scrollTo(0, 0);
    setUser(auth.user);
  }, [auth.user]);

  const handleCancel = () => {
    setUser(auth.user);
    setEnabled(false);
    setError(false);
    setSuccess(false);
    setIsPasswordUpdating(false);
  };

  const handleEdit = () => {
    setEnabled(true);
    setSuccess(false);
    setError(false);
  };

  const handleChangePassword = () => {
    setUser({ ...user, password: "" });
    setIsPasswordUpdating(true);
  };

  const handleSubmit = () => {
    const formData = new FormData();

    // Add user data to formData - stringify the user object
    formData.append("user", JSON.stringify(user));
    formData.append("hasPassword", isPasswordUpdating);
    formData.append("newPassword", newPassword);
    formData.append("newPasswordRepeated", newPasswordRepeated);

    // Add cover file if it exists
    if (cover) {
      formData.append("cover", cover);
    }

    const csrftoken = Cookies.get("csrftoken");
    const headers = {
      Authorization: localStorage.getItem("jwt"),
      "Content-Type": "multipart/form-data",
      "X-CSRFToken": csrftoken,
    };

    const promise = postRequest("updateUser/", formData, headers);
    promise.then((res) => {
      setSuccess(true);
      setError(false);
      setEnabled(false);
      setIsPasswordUpdating(false);
      window.location.href = "/account/Account";
    })
      .catch((err) => {
        console.log(err);
        setSuccess(false);
        setError(true);
      });
  };

  const onCoverEdit = () => {
    const coverInput = document.getElementById("cover-upload");
    if (coverInput) {
      coverInput.click();
    }
  }

  const inputStyle = {
    border: enabled ? "2px solid #f2f2f2" : "none",
    borderRadius: "10px",
    fontSize: "16px",
    fontWeight: "500",
    paddingLeft: !enabled && "0",
    paddingTop: !enabled && "0",
    paddingBottom: !enabled && "0",
    background: !enabled && PALETTE.WHITE,
    textAlign: "left"
  }

  // Special style for date inputs to fix iOS alignment issues
  const dateInputStyle = {
    ...inputStyle,
    textAlign: "left !important",
    direction: "ltr",
    WebkitAppearance: "none",
    appearance: "none",
    paddingRight: "10px"
  }

  return (
    <div style={{ paddingBottom: 20 }}>
      <SettingsHeader
        text={enabled ? "Salva" : "Modifica"}
        onClick={enabled ? handleSubmit : handleEdit}
      />
      <ContainerResponsive>
        <div
          className="align-self-center text-center m-auto">
          <Form
            className="p-2"
            action={"/account/Profile"}
            onSubmit={handleSubmit}
          >
            <Form.Group className="mb-4">
              <div className="position-relative">
                <AccountAvatar
                  firstname={user.firstname}
                  lastname={user.lastname}
                  size={80}
                  fontSize={28}
                  completionPercentage={percentage}
                  editable={enabled}
                  onEdit={onCoverEdit}
                  cover={coverPreview}
                />

                {/* Cover image preview and edit button */}
                <div className="d-flex align-items-center">
                  <div>
                    <Form.Control
                      type="file"
                      accept="image/*"
                      onChange={(event) => {
                        const selectedFile = event.target.files[0];
                        if (selectedFile) {
                          setCover(selectedFile);
                          const objectUrl = URL.createObjectURL(selectedFile);
                          setCoverPreview(objectUrl);
                        }
                      }}
                      style={{ display: "none" }}
                      id="cover-upload"
                      disabled={!enabled}
                    />
                  </div>
                </div>
              </div>
            </Form.Group>

            <Container className="rounded-md py-3 px-3" style={{ background: PALETTE.WHITE }}>
              {/* Nome */}
              <Row className="mb-4 align-items-center">
                <Col xs={2} className="text-center">
                  <FaUser size={20} color={PALETTE.DARK} />
                </Col>
                <Col xs={10}>
                  <div className="text-start">
                    <span style={{ fontSize: '12px', color: PALETTE.GRAY }}>Nome</span>
                  </div>
                  <Form.Control
                    type="text"
                    value={user.firstname}
                    onChange={(event) => {
                      setUser({ ...user, firstname: event.target.value });
                    }}
                    style={inputStyle}
                    placeholder=""
                    disabled={!enabled}
                    required
                  />
                </Col>
              </Row>

              {/* Cognome */}
              <Row className="mb-4 align-items-center rounded-sm">
                <Col xs={2} className="text-center">
                  <FaUser size={20} color={PALETTE.DARK} />
                </Col>
                <Col xs={10}>
                  <div className="text-start">
                    <span style={{ fontSize: '12px', color: PALETTE.GRAY }}>Cognome</span>
                  </div>
                  <Form.Control
                    type="text"
                    value={user.lastname}
                    onChange={(event) => {
                      setUser({ ...user, lastname: event.target.value });
                    }}
                    style={inputStyle}
                    placeholder=""
                    disabled={!enabled}
                    required
                  />
                </Col>
              </Row>

              {/* Data di nascità */}
              <Row className="mb-4 align-items-center">
                <Col xs={2} className="text-center">
                  <FaCalendarAlt size={20} color={PALETTE.DARK} />
                </Col>
                <Col xs={10}>
                  <div className="text-start">
                    <span style={{ fontSize: '12px', color: PALETTE.GRAY }}>Data di nascità</span>
                  </div>
                  {enabled ? (
                    <Form.Control
                      type="date"
                      value={user.birthdate}
                      onChange={(event) => {
                        setUser({ ...user, birthdate: event.target.value });
                      }}
                      style={dateInputStyle}
                      placeholder=""
                      required
                    />
                  ) : (
                    <span style={{
                      ...inputStyle,
                      display: "block",
                      fontSize: "16px",
                      textAlign: "left",
                      color: PALETTE.BLACK
                    }}>
                      {user.birthdate ? user.birthdate.split('-').reverse().join('/') : ''}
                    </span>
                  )}
                </Col>
              </Row>

              {/* Sesso */}
              <Row className="mb-4 align-items-center">
                <Col xs={2} className="text-center">
                  <FaVenusMars size={20} color={PALETTE.DARK} />
                </Col>
                <Col xs={10}>
                  <div className="text-start">
                    <span style={{ fontSize: '12px', color: PALETTE.GRAY }}>Sesso</span>
                  </div>
                  <Form.Select
                    style={inputStyle}
                    aria-label="sex"
                    disabled={!enabled}
                    onChange={(event) => {
                      setUser({ ...user, sex: event.target.value });
                    }}
                    value={user.sex}
                  >
                    <option key={"M"} value={"M"}>
                      {"Maschio"}
                    </option>
                    <option key={"F"} value={"F"}>
                      {"Femmina"}
                    </option>
                  </Form.Select>
                </Col>
              </Row>

              {/* Altezza */}
              <Row className="mb-4 align-items-center">
                <Col xs={2} className="text-center">
                  <GiBodyHeight size={20} color={PALETTE.DARK} />
                </Col>
                <Col xs={10}>
                  <div className="text-start">
                    <span style={{ fontSize: '12px', color: PALETTE.GRAY }}>Altezza (CM)</span>
                  </div>
                  <Form.Control
                    type="number"
                    autoComplete="height"
                    value={user.height}
                    onChange={(event) => {
                      setUser({ ...user, height: event.target.value });
                    }}
                    style={inputStyle}
                    placeholder=""
                    disabled={!enabled}
                    required
                  />
                </Col>
              </Row>

              {/* Peso */}
              <Row className="mb-4 align-items-center">
                <Col xs={2} className="text-center">
                  <FaWeight size={20} color={PALETTE.DARK} />
                </Col>
                <Col xs={10}>
                  <div className="text-start">
                    <span style={{ fontSize: '12px', color: PALETTE.GRAY }}>Peso (KG)</span>
                  </div>
                  <Form.Control
                    type="number"
                    autoComplete="weight"
                    value={user.weight}
                    onChange={(event) => {
                      setUser({ ...user, weight: event.target.value });
                    }}
                    style={inputStyle}
                    placeholder=""
                    disabled={!enabled}
                    required
                  />
                </Col>
              </Row>

              {/* Codice Fiscale */}
              <Row className="mb-4 align-items-center">
                <Col xs={2} className="text-center">
                  <FaIdCard size={20} color={PALETTE.DARK} />
                </Col>
                <Col xs={10}>
                  <div className="text-start">
                    <span style={{ fontSize: '12px', color: PALETTE.GRAY }}>
                      Codice Fiscale{" "}
                      {warning && (
                        <div
                          className="badge rounded-pill blink"
                          style={{
                            width: 16,
                            height: 16,
                            marginTop: -4,
                            position: "absolute",
                            background: PALETTE.LINEARGRADIENT,
                            border: "solid 1px " + PALETTE.LIGHTGRAY,
                          }}
                        />
                      )}
                    </span>
                  </div>
                  <Form.Control
                    type="text"
                    value={user.cf}
                    onChange={(event) => {
                      setUser({ ...user, cf: event.target.value });
                    }}
                    style={inputStyle}
                    placeholder=""
                    disabled={!enabled}
                    required
                  />
                </Col>
              </Row>

              {/* Email */}
              <Row className="mb-4 align-items-center">
                <Col xs={2} className="text-center">
                  <FaEnvelope size={20} color={PALETTE.DARK} />
                </Col>
                <Col xs={10}>
                  <div className="text-start">
                    <span style={{ fontSize: '12px', color: PALETTE.GRAY }}>Email</span>
                  </div>
                  <Form.Control
                    type="email"
                    autoComplete="username"
                    value={user.email}
                    onChange={(event) => {
                      setUser({ ...user, email: event.target.value });
                    }}
                    style={inputStyle}
                    placeholder=""
                    disabled={!enabled}
                    required
                  />
                </Col>
              </Row>

              {/* Password */}
              <Row className={`align-items-center ${isPasswordUpdating && "mb-4"}`}>
                <Col xs={2} className="text-center">
                  <FaLock size={20} color={PALETTE.DARK} />
                </Col>
                <Col xs={10}>
                  <div className="text-start">
                    <span style={{ fontSize: '12px', color: PALETTE.GRAY }}>Password</span>
                  </div>
                  <Form.Control
                    type="password"
                    autoComplete="new-password"
                    value={user.password}
                    placeholder="**************"
                    onChange={(event) => {
                      setUser({ ...user, password: event.target.value });
                    }}
                    style={inputStyle}
                    disabled={!isPasswordUpdating}
                    required
                  />
                </Col>
              </Row>
              {enabled && !isPasswordUpdating && (
                <div className="text-end mb-2">
                  <span
                    style={{
                      textDecoration: "underline",
                      color: PALETTE.DARKGRAY,
                      cursor: "pointer",
                    }}
                    onClick={handleChangePassword}
                  >
                    Cambia password
                  </span>
                </div>
              )}
              {enabled && isPasswordUpdating && (
                <div>
                  {/* Nuova Password */}
                  <Row className="mb-4 align-items-center">
                    <Col xs={2} className="text-center">
                      <FaLock size={20} color={PALETTE.DARK} />
                    </Col>
                    <Col xs={10}>
                      <div className="text-start">
                        <span style={{ fontSize: '12px', color: PALETTE.GRAY }}>Nuova Password</span>
                      </div>
                      <Form.Control
                        type="password"
                        autoComplete="new-password"
                        value={newPassword}
                        onChange={(event) => {
                          setNewPassword(event.target.value);
                        }}
                        style={{
                          border: "2px solid #f2f2f2",
                          borderRadius: "10px",
                          fontSize: "16px",
                          fontWeight: "500"
                        }}
                        placeholder=""
                        required
                      />
                    </Col>
                  </Row>

                  {/* Ripeti nuova Password */}
                  <Row className="align-items-center">
                    <Col xs={2} className="text-center">
                      <FaLock size={20} color={PALETTE.DARK} />
                    </Col>
                    <Col xs={10}>
                      <div className="text-start">
                        <span style={{ fontSize: '12px', color: PALETTE.GRAY }}>Ripeti nuova Password</span>
                      </div>
                      <Form.Control
                        type="password"
                        autoComplete="new-password"
                        value={newPasswordRepeated}
                        onChange={(event) => {
                          setNewPasswordRepeated(event.target.value);
                        }}
                        style={{
                          border: "2px solid #f2f2f2",
                          borderRadius: "10px",
                          fontSize: "16px",
                          fontWeight: "500"
                        }}
                        placeholder=""
                        required
                      />
                    </Col>
                  </Row>
                </div>
              )}
              {success && (
                <p style={{ color: "green" }}>Profilo aggiornato con successo</p>
              )}
              {error && (
                <p style={{ color: "red" }}>Errore durante l'aggiornamento</p>
              )}
            </Container>
          </Form>
        </div>
      </ContainerResponsive>
    </div>
  );
}
